import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  RankAdminClient,
  RankClientEvolutionResponseDto,
} from '@/api/rank-admin-client';
import { useRankAdminClient } from '@/api/rank-admin-client-context';

// =============================================================================
// use-rank-client-evolution.ts — Hook TanStack Query de l'évolution nominative par client (R25, PHASE R).
// =============================================================================
// Lecture (useQuery) des blocs d'évolution pour un ENSEMBLE de campagnes (résolu FRONT par le SIREN
// sélectionné — C5). La clé de query intègre les campaignId TRIÉS (stabilité du cache indépendante de
// l'ordre d'entrée). `enabled` = au moins une campagne (pas de fetch tant qu'aucun client/campagne).
// ZÉRO règle métier ici : l'autorité (RBAC AdminSessionGuard, composition ACL, médiane) est l'API.

const RANK_EVOLUTION_KEY = ['rank-admin', 'client-evolution'] as const;

/** Clé de query stable (campaignId triés — indépendante de l'ordre de sélection). */
export function rankClientEvolutionKey(
  campaignIds: readonly string[],
): readonly unknown[] {
  return [...RANK_EVOLUTION_KEY, [...campaignIds].sort((a, b) => a.localeCompare(b))];
}

/**
 * Évolution nominative du prix retenu pour l'ensemble `campaignIds` (résolu FRONT par SIREN). Désactivé
 * tant qu'aucune campagne (aucun client sélectionné / aucune campagne éligible) : évite un fetch inutile.
 */
export function useRankClientEvolution(
  campaignIds: readonly string[],
): UseQueryResult<RankClientEvolutionResponseDto, Error> {
  const client: RankAdminClient = useRankAdminClient();
  return useQuery<RankClientEvolutionResponseDto, Error>({
    queryKey: rankClientEvolutionKey(campaignIds),
    queryFn: () => client.getRankClientEvolution(campaignIds),
    enabled: campaignIds.length > 0,
  });
}
